import React, { useState, useCallback, useEffect } from 'react'
import {
  Drawer, Form, Button, Space, Select, Input, InputNumber, Table,
  Alert, message, Divider
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { stockTakingService } from '@services/operator/stockTakingService'
import { merchantService } from '@services/operator/merchantService'
import { inventoryTypeService } from '@services/operator/inventoryTypeService'

// 後端的硬上限：一張虛擬倉盤點單最多 MAX_ITEMS 個 inventory。FE 以「抓到 N+1
// 就停並擋送出」的邏輯對齊（所見即所建：要嘛全部送、要嘛不能送）。
const MAX_ITEMS = 1000
// PAGE_SIZE 對齊後端 ApiResponse::MAX_PAGE_SIZE（200）；送更大值會被 clamp。
const PAGE_SIZE = 200
const MAX_PAGE_LOOPS = Math.ceil(MAX_ITEMS / PAGE_SIZE) + 1 // 多載一頁用來偵測截斷

// Props:
//   open: boolean
//   onClose: () => void
//   onCreated?: (stockTakingId: string) => void   // 不傳則 navigate 到 Show
export default function VirtualCreate({ open, onClose, onCreated }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const [merchants, setMerchants]               = useState([])
  const [loadingInventoryTypes, setLoadingInventoryTypes] = useState(false)
  const [inventoryTypes, setInventoryTypes]     = useState([])
  const [loadingPreview, setLoadingPreview]     = useState(false)
  const [loadingCreate, setLoadingCreate]       = useState(false)
  const [previewRows, setPreviewRows]           = useState([])
  const [previewLoaded, setPreviewLoaded]       = useState(false)
  const [previewTruncated, setPreviewTruncated] = useState(false)

  // 開啟時載入可存取的貨主（自動分頁載完）
  useEffect(() => {
    if (!open || merchants.length > 0) return
    setLoadingMerchants(true)
    merchantService.fetchAllWarehouseMerchants()
      .then((res) => {
        if (res?.success) {
          setMerchants(res.data || [])
        } else {
          console.error('[VirtualCreate] fetchAllWarehouseMerchants failed:', res)
          message.error(t('common.error.fetch_merchants_failed'))
        }
      })
      .catch((err) => {
        console.error('[VirtualCreate] fetchAllWarehouseMerchants error:', err)
        message.error(t('common.error.fetch_merchants_failed'))
      })
      .finally(() => setLoadingMerchants(false))
  }, [open, merchants.length, t])

  // 開啟時載入庫存類型（組織級，一次載完、下拉直接挑）
  useEffect(() => {
    if (!open || inventoryTypes.length > 0) return
    setLoadingInventoryTypes(true)
    inventoryTypeService.listAll()
      .then((res) => {
        if (res?.success) {
          setInventoryTypes(res.data || [])
        } else {
          console.error('[VirtualCreate] inventoryTypeService.listAll failed:', res)
          message.error(t('common.error.fetch_failed'))
        }
      })
      .catch((err) => {
        console.error('[VirtualCreate] inventoryTypeService.listAll error:', err)
        message.error(t('common.error.fetch_failed'))
      })
      .finally(() => setLoadingInventoryTypes(false))
  }, [open, inventoryTypes.length, t])

  const resetAll = useCallback(() => {
    form.resetFields()
    setPreviewRows([])
    setPreviewLoaded(false)
    setPreviewTruncated(false)
  }, [form])

  const handleClose = () => {
    resetAll()
    onClose()
  }

  const handlePreview = async () => {
    try {
      await form.validateFields(['merchantId'])
    } catch {
      return
    }
    setLoadingPreview(true)
    try {
      const values = form.getFieldsValue()
      const filter = buildFilter(values)
      const merchantId = values.merchantId

      const all = []
      let sinceId = null
      let hasMore = true
      let loops = 0
      let truncated = false

      while (hasMore && loops < MAX_PAGE_LOOPS) {
        loops += 1
        const res = await stockTakingService.previewVirtualScope({
          merchantId, filter, pageSize: PAGE_SIZE, sinceId
        })
        if (!res.success) {
          // 後端 error code 走 errors.${code} i18n；未知 code 退回 fetch_failed。
          // interpolations 必須展開給 t()，否則 {{count}} 等 placeholder 會裸露給使用者。
          console.error('[VirtualCreate] previewVirtualScope failed:', res)
          const code = res.message
          const msg  = code
            ? t(`errors.${code}`, { ...(res.interpolations || {}), defaultValue: t('common.error.fetch_failed') })
            : t('common.error.fetch_failed')
          message.error(msg)
          setPreviewRows([])
          setPreviewLoaded(false)
          setPreviewTruncated(false)
          return
        }
        all.push(...(res.data || []))
        hasMore = !!res.has_more
        sinceId = res.since_id

        // 一旦超過 MAX_ITEMS 就停：再多抓也送不出去，徒增 DB 負擔。
        if (all.length > MAX_ITEMS) {
          truncated = true
          break
        }
      }

      // 迴圈用完時若 hasMore 仍為 true，代表後端還有資料——同樣視為截斷。
      if (!truncated && hasMore) {
        truncated = true
      }

      setPreviewRows(all.slice(0, MAX_ITEMS + 1)) // 多保一筆讓 UI 有機會顯示「>1000」
      setPreviewLoaded(true)
      setPreviewTruncated(truncated)

      if (truncated) {
        message.warning(
          t('shared.stock_takings.virtual_create.preview_truncated', { max: MAX_ITEMS })
        )
      }
    } catch (err) {
      console.error('[VirtualCreate] handlePreview error:', err)
      message.error(t('common.error.fetch_failed'))
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleCreate = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    if (!previewLoaded || previewRows.length === 0) {
      message.error(t('shared.stock_takings.virtual_create.preview_required'))
      return
    }
    // 預覽被截斷（筆數 > MAX_ITEMS）時禁止送出：使用者必須先縮小條件，
    // 才能讓「所見即所建」的契約成立。
    if (previewTruncated || previewRows.length > MAX_ITEMS) {
      message.error(t('shared.stock_takings.virtual_create.narrow_filter_required'))
      return
    }

    setLoadingCreate(true)
    try {
      const inventoryIds = previewRows.map((r) => r.inventory_id)
      const res = await stockTakingService.generateForVirtual({
        merchantId:   values.merchantId,
        inventoryIds,
      })
      if (!res.success) {
        // interpolations 必須展開給 t()，否則 stock_taking_stale_inventories 等帶
        // {{count}} placeholder 的 error 會裸露 placeholder 給使用者。
        console.error('[VirtualCreate] generateForVirtual failed:', res)
        const code = res.message
        const msg  = code
          ? t(`errors.${code}`, { ...(res.interpolations || {}), defaultValue: t('common.error.create_failed') })
          : t('common.error.create_failed')
        message.error(msg)
        return
      }
      message.success(t('shared.stock_takings.virtual_create.create_success'))
      resetAll()
      onClose()
      if (onCreated) {
        onCreated(res.data.id)
      } else {
        navigate(`/stock_takings/${res.data.id}`)
      }
    } catch (err) {
      console.error('[VirtualCreate] handleCreate error:', err)
      message.error(t('common.error.create_failed'))
    } finally {
      setLoadingCreate(false)
    }
  }

  const submitDisabled = !previewLoaded || previewRows.length === 0 || previewTruncated || previewRows.length > MAX_ITEMS

  return (
    <Drawer
      title={t('shared.stock_takings.virtual_create.title')}
      width={640}
      open={open}
      onClose={handleClose}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={handleClose}>{t('common.button.cancel')}</Button>
          <Button onClick={handlePreview} loading={loadingPreview}>
            {t('shared.stock_takings.virtual_create.preview')}
          </Button>
          <Button
            type="primary"
            onClick={handleCreate}
            loading={loadingCreate}
            disabled={submitDisabled}
          >
            {t('shared.stock_takings.virtual_create.submit')}
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        message={t('shared.stock_takings.virtual_create.hint')}
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="merchantId"
          label={t('shared.stock_takings.virtual_create.merchant')}
          rules={[{ required: true, message: t('common.form.required') }]}
        >
          <Select
            showSearch
            loading={loadingMerchants}
            optionFilterProp="label"
            placeholder={t('shared.stock_takings.virtual_create.merchant_placeholder')}
            options={merchants.map((m) => ({ value: m.id, label: m.name || m.code || m.id }))}
          />
        </Form.Item>

        <Divider />

        <Form.Item name="keyword" label={t('shared.stock_takings.virtual_create.keyword')}>
          <Input allowClear placeholder={t('shared.stock_takings.virtual_create.keyword_placeholder')} />
        </Form.Item>

        <Form.Item name="productActivated" label={t('shared.stock_takings.virtual_create.product_status')}>
          <Select
            allowClear
            options={[
              { value: true,  label: t('shared.stock_takings.virtual_create.product_status_active') },
              { value: false, label: t('shared.stock_takings.virtual_create.product_status_inactive') }
            ]}
          />
        </Form.Item>

        <Form.Item name="inventoryTypeIds" label={t('shared.stock_takings.virtual_create.inventory_type')}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            loading={loadingInventoryTypes}
            optionFilterProp="label"
            placeholder={t('shared.stock_takings.virtual_create.inventory_type_placeholder')}
            options={inventoryTypes.map((it) => ({ value: it.id, label: it.name || it.code || it.id }))}
          />
        </Form.Item>

        <Space align="start" wrap>
          <Form.Item name="movementHas" label={t('shared.stock_takings.virtual_create.movement_mode')}>
            <Select
              allowClear
              style={{ width: 200 }}
              options={[
                { value: true,  label: t('shared.stock_takings.virtual_create.movement_has') },
                { value: false, label: t('shared.stock_takings.virtual_create.movement_none') }
              ]}
            />
          </Form.Item>
          <Form.Item name="movementSinceDays" label={t('shared.stock_takings.virtual_create.movement_days')}>
            <InputNumber min={1} max={365} style={{ width: 140 }} />
          </Form.Item>
        </Space>
      </Form>

      {previewLoaded && (
        <Alert
          style={{ marginBottom: 12 }}
          type={previewTruncated || previewRows.length > MAX_ITEMS ? 'warning' : 'success'}
          showIcon
          message={t('shared.stock_takings.virtual_create.preview_summary', {
            count: previewTruncated ? `${MAX_ITEMS}+` : previewRows.length
          })}
        />
      )}

      <Table
        size="small"
        dataSource={previewRows.slice(0, MAX_ITEMS)}
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        columns={[
          { title: t('shared.stock_takings.virtual_create.col_sku'),             dataIndex: 'sku' },
          { title: t('shared.stock_takings.virtual_create.col_name'),            dataIndex: 'product_name' },
          { title: t('shared.stock_takings.virtual_create.col_inventory_type'),  dataIndex: 'inventory_type_name' },
          { title: t('shared.stock_takings.virtual_create.col_batch'),           dataIndex: 'batch' },
          { title: t('shared.stock_takings.virtual_create.col_expiration_date'), dataIndex: 'expiration_date' },
          { title: t('shared.stock_takings.virtual_create.col_on_hand'),         dataIndex: 'on_hand', align: 'right' }
        ]}
      />
    </Drawer>
  )
}

// --- helpers ---

function buildFilter(values) {
  const filter = {}
  if (values.keyword?.trim())             filter.keyword           = values.keyword.trim()
  if (values.productActivated !== undefined && values.productActivated !== null) {
    filter.product_activated = values.productActivated
  }
  if (Array.isArray(values.inventoryTypeIds) && values.inventoryTypeIds.length > 0) {
    filter.inventory_type_ids = values.inventoryTypeIds
  }
  if (values.movementHas !== undefined && values.movementHas !== null && values.movementSinceDays) {
    filter.movement = {
      has_movement: values.movementHas,
      since_days:   values.movementSinceDays
    }
  }
  return filter
}
